import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { caseTypesApi, extractOne, extractList } from '../../api/facades';

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const CaseTypeEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = id ? parseInt(id, 10) : NaN;

  const [case_type_name, setCaseTypeName] = useState('');
  const [parent_id, setParentId] = useState<number | ''>('');
  const [is_active, setIsActive] = useState<'Yes' | 'No'>('Yes');

  const { data: res, isLoading } = useQuery({
    queryKey: ['case-types', numId],
    queryFn: () => caseTypesApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const row = extractOne(res);

  const { data: parentsRes } = useQuery({
    queryKey: ['case-types'],
    queryFn: () => caseTypesApi.list({}),
  });
  const parents = extractList(parentsRes).filter((p: any) => p.id !== numId);

  useEffect(() => {
    if (!row || typeof row !== 'object') return;
    if ((row as any).case_type_name != null) setCaseTypeName(String((row as any).case_type_name));
    if ((row as any).parent_id != null) setParentId((row as any).parent_id); else setParentId('');
    if ((row as any).is_active != null) setIsActive((row as any).is_active === 'No' ? 'No' : 'Yes');
  }, [row]);

  const update = useMutation({
    mutationFn: () =>
      caseTypesApi.update(numId, {
        case_type_name: case_type_name || undefined,
        parent_id: parent_id === '' ? null : parent_id,
        is_active,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-types'] });
      toast.success('Case type updated.');
      navigate(`/dashboard/settings/case-types/${numId}`);
    },
    onError: (e: any) => toast.error(e?.message || 'Update failed.'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!case_type_name.trim()) return;
    update.mutate();
  };

  if (Number.isNaN(numId)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border"><p className="text-slate-600">Invalid ID.</p><Button variant="secondary" className="mt-4" onClick={() => navigate(-1)}>Back</Button></div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4">Edit Case Type</h1>
      {isLoading ? (
        <p className="text-slate-500">Loading…</p>
      ) : (
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <FormField label="Name">
              <input className={inputClass} value={case_type_name} onChange={(e) => setCaseTypeName(e.target.value)} placeholder="e.g. Civil" required />
            </FormField>
          </div>
          <FormField label="Parent (optional)">
            <select className={inputClass} value={parent_id === '' ? '' : parent_id} onChange={(e) => setParentId(e.target.value === '' ? '' : Number(e.target.value))}>
              <option value="">— None —</option>
              {parents.map((p: any) => <option key={p.id} value={p.id}>{p.case_type_name}</option>)}
            </select>
          </FormField>
          <FormField label="Active">
            <select className={inputClass} value={is_active} onChange={(e) => setIsActive(e.target.value as 'Yes' | 'No')}>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </FormField>
          <div className="md:col-span-2 flex gap-2 mt-2">
            <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={!case_type_name.trim() || update.isPending}>{update.isPending ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default CaseTypeEditPage;
